import React from "react";
import { Link, useParams } from "react-router-dom";
import moment from "moment-timezone";

import {
  ViewPermission,
  ModifyPermission,
  BillingPermission,
  APIPermission,
  ManagePermission,
  WorkbooksPermission,
} from "ui/DocsSnippet";
import MemberAlertDestinations from "features/Checks/MemberAlertDestinations";
import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PanelTable from "ui/PanelTable";
import PermissionIcon from "features/Organization/PermissionIcon";
import Loading from "ui/Loading";
import CopyToClipboard from "ui/CopyToClipboard";
import { useAppConfig } from "providers/WithAppConfig";

import styles from "features/Organization/MemberDetails/style.module.scss";
import { useRoutes } from "utils/routes";
import { useAsyncActionFlash } from "providers/WithFlashes";
import { getPermissionKind } from "utils/permissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import {
  useMemberDetailsQuery,
  MemberDetailsQuery,
} from "features/Organization/MemberDetails/gql/Query.generated";
import { useDeleteOrganizationMemberMutationMutation } from "features/Organization/MemberDetails/gql/Mutation.generated";

type MembershipDetailsType =
  MemberDetailsQuery["getOrganizationMembershipDetails"];
type PermissionType = MembershipDetailsType["permissions"][number];

const MemberDetails: React.FunctionComponent = () => {
  const { slug: organizationSlug, id: membershipId } = useParams();
  const { organizationMemberEdit, organizationResendInvite } = useRoutes();
  const appConfig = useAppConfig();
  const { data, loading, error } = useMemberDetailsQuery({
    variables: { membershipId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }
  const member = data.getOrganizationMembershipDetails;
  const { permissions, permittedToEdit } = member;

  const inviteUrl = member.invitePath
    ? window.location.origin + member.invitePath
    : null;

  return (
    <PageContent
      title={`Organization Member: ${
        member.accepted ? member.user.fullname : member.email
      }`}
      pageCategory="organization-members"
      pageName="show"
    >
      <Panel title="Member Details">
        <PanelSection>
          <dl>
            <dt>Name:</dt>
            <dd>{member.accepted ? member.user.fullname : "-"}</dd>
            <dt>Email:</dt>
            <dd>{member.email}</dd>
            <dt>{member.accepted ? "Joined:" : "Invited:"}</dt>
            <dd>{moment.unix(member.createdAt).format("YYYY-MM-DD")}</dd>
          </dl>
        </PanelSection>
        {!member.accepted && (
          <PanelSection>
            <p>
              {member.expired
                ? "This invitation has expired."
                : "This member has not accepted their invite yet."}
            </p>
            {permittedToEdit ? (
              <>
                <p className="flex gap-4">
                  {appConfig.emailIntegrationAvailable && (
                    <Link
                      to={organizationResendInvite(organizationSlug, member.id)}
                      data-method="post"
                      title={`Resend invite to ${member.email}`}
                      className="btn btn-success"
                    >
                      Resend invite email
                    </Link>
                  )}
                  {!member.accepted && (
                    <RemoveMembershipButton
                      organizationSlug={organizationSlug}
                      member={member}
                    >
                      {member.expired
                        ? "Delete invitation"
                        : "Cancel invitation"}
                    </RemoveMembershipButton>
                  )}
                </p>
                {!member.expired && (
                  <>
                    <strong>Share this invite URL with them:</strong>
                    <CopyToClipboard
                      className="btn btn-small btn-link"
                      content={inviteUrl}
                      label="Copy to clipboard"
                    />
                  </>
                )}
              </>
            ) : (
              <p>
                You don't have the <strong>Manage</strong> permission needed to
                access member invites.
              </p>
            )}
            {!member.expired && <pre>{inviteUrl}</pre>}
          </PanelSection>
        )}
      </Panel>

      <MemberAlertDestinations
        organizationSlug={organizationSlug}
        membershipId={member.id}
      />

      <Panel title="Assigned Roles & Permissions">
        <PanelTable className="max-w-7xl">
          <thead>
            <tr>
              <th>Role</th>
              <th>Scope</th>
              <th className={styles.permission}>
                View <ViewPermission />
              </th>
              <th className={styles.permission}>
                Modify <ModifyPermission />
              </th>
              <th className={styles.permission}>
                Create Workbooks <WorkbooksPermission />
              </th>
              <th className={styles.permission}>
                Billing <BillingPermission />
              </th>
              <th className={styles.permission}>
                API <APIPermission />
              </th>
              <th className={styles.permission}>
                Manage <ManagePermission />
              </th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((p) => (
              <MemberPermission
                key={p.id}
                permission={p}
                organizationSlug={organizationSlug}
              />
            ))}
          </tbody>
        </PanelTable>
        {permittedToEdit && (
          <PanelSection>
            <Link
              to={organizationMemberEdit(organizationSlug, member.id)}
              className="btn btn-success"
            >
              Edit Assigned Roles
            </Link>
          </PanelSection>
        )}
      </Panel>
      {member.accepted && (
        <RemoveMembership member={member} organizationSlug={organizationSlug} />
      )}
    </PageContent>
  );
};

const RemoveMembership: React.FunctionComponent<{
  member: MembershipDetailsType;
  organizationSlug: string;
}> = ({ member, organizationSlug }) => {
  const { permittedToRemove, userIsSelf } = member;

  if (!permittedToRemove && !userIsSelf) {
    return null;
  }

  if (!permittedToRemove && userIsSelf) {
    return (
      <Panel title="Leave Organization">
        <PanelSection>
          You cannot leave this organization as the only member with the{" "}
          <strong>Manage</strong> permission.
        </PanelSection>
      </Panel>
    );
  }

  return (
    <Panel title={userIsSelf ? "Leave Organization" : "Removal"}>
      <form>
        <p>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          {userIsSelf ? (
            <span>
              Leaving this organization will immediately revoke all your
              permissions and you will need a new invitation to join this
              organization again.
            </span>
          ) : (
            <span>
              Removing this member will remove all assigned roles from them and
              remove them from the organization.
            </span>
          )}
        </p>
        <p>
          <span className="block form-group">
            <RemoveMembershipButton
              member={member}
              organizationSlug={organizationSlug}
            >
              {userIsSelf ? "Leave Organization" : "Remove Member"}
            </RemoveMembershipButton>
          </span>
        </p>
      </form>
    </Panel>
  );
};

function RemoveMembershipButton({
  member,
  organizationSlug,
  children,
}: {
  member: MembershipDetailsType;
  organizationSlug: string;
  children: React.ReactNode;
}) {
  const { userIsSelf } = member;

  const [
    deleteMember,
    { called: mutationCalled, loading: mutationLoading, error: mutationError },
  ] = useDeleteOrganizationMemberMutationMutation();

  const { root, organizationMembers } = useRoutes();

  useAsyncActionFlash({
    called: mutationCalled,
    loading: mutationLoading,
    error: mutationError?.message,
    success: `Removed ${
      member.user ? member.user.fullname : "invited member"
    } from the organization`,
    successRedirect: userIsSelf
      ? root()
      : organizationMembers(organizationSlug),
  });
  const handleDelete = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    if (window.confirm(`Remove ${member.email} from this organization?`)) {
      deleteMember({ variables: { membershipId: member.id } });
    }
  };
  return (
    <button className="btn btn-danger" onClick={handleDelete}>
      {children}
    </button>
  );
}

const MemberPermission: React.FunctionComponent<{
  permission: PermissionType;
  organizationSlug: string;
}> = ({ permission, organizationSlug }) => {
  const {
    id,
    database,
    server,
    view,
    modify,
    billing,
    api,
    manage,
    createWorkbooks,
    restricted,
    role,
  } = permission;

  const { organizationRole } = useRoutes();
  return (
    <tr key={id}>
      <td>
        <Link to={organizationRole(organizationSlug, role.id)}>
          {role.name}
        </Link>
      </td>
      <td>
        {database
          ? "Database: " + database.displayName
          : server
          ? "Server: " + server.name
          : "All Databases/Servers"}
      </td>
      <td className={styles.permission}>
        <PermissionIcon permission={getPermissionKind(view, restricted)} />
      </td>
      <td className={styles.permission}>
        <PermissionIcon permission={getPermissionKind(modify, restricted)} />
      </td>
      <td className={styles.permission}>
        <PermissionIcon
          permission={getPermissionKind(createWorkbooks, restricted)}
        />
      </td>
      <td className={styles.permission}>
        <PermissionIcon permission={getPermissionKind(billing, restricted)} />
      </td>
      <td className={styles.permission}>
        <PermissionIcon permission={getPermissionKind(api, restricted)} />
      </td>
      <td className={styles.permission}>
        <PermissionIcon permission={getPermissionKind(manage, restricted)} />
      </td>
    </tr>
  );
};

export default MemberDetails;
